import styled from '@emotion/styled';
import { IconCheck, IconLetter, IconPhoneCall } from '@kompare/kompare-ui-library';
import { formatPrice, i18n, PropertyTypeEnum, resolvePremiums } from 'src/common';
import { GetOfferQuery } from 'src/common/api/apollo/types/types';
import config from 'src/config';
import { colors } from 'src/theme';
import { Button, Card, Divider, Flex, Text } from 'src/components';

export type ThankYouVariant = 'online' | 'offline';

export interface ThankYouCardProps {
  offer?: GetOfferQuery['getOffer'];
  variant?: ThankYouVariant;
}

const ContainerStyle = styled(Flex)({
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
});

const SuccessBadgeStyle = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 72,
  height: 72,
  borderRadius: '50%',
  backgroundColor: colors.success2,
});

const CardTitleStyle = styled(Text)({
  marginBottom: 4,
});

const InfoLinkStyle = styled.a({
  textDecoration: 'none',
});

const DetailRow = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <Flex justify="space-between" align="flex-start" gap={16}>
    <Text type="small" color={colors.text3}>
      {label}
    </Text>
    <Flex vertical align="flex-end">
      <Text type="base" bold color={colors.text1}>
        {value}
      </Text>
      {note && (
        <Text type="tiny" color={colors.text4}>
          {note}
        </Text>
      )}
    </Flex>
  </Flex>
);

export const ThankYouCard = ({ offer, variant = 'online' }: ThankYouCardProps) => {
  const t = (key: string) => i18n.t(`pages.checkoutCongratulationsPage.${key}`);

  const navigateToHome = () => {
    window.location.href = config.REACT_APP_KOMPARE_HOME ?? '/';
  };

  const pts = offer?.productTypeSpecific;
  const payment = pts?.payment;
  const paymentMethod = payment?.paymentMethod ?? '';
  const isInstallments = paymentMethod.toLowerCase().includes('installments');
  const { annual, installmentsTotal, monthly } = resolvePremiums(payment);
  const amount = isInstallments ? installmentsTotal : annual;

  const propertyTypes = [
    ...(pts?.building?.package?.propertyType ?? []),
    ...(pts?.household?.package?.propertyType ?? []),
  ];
  const subject = propertyTypes.includes(PropertyTypeEnum.HOUSE)
    ? t('subjectHouse')
    : propertyTypes.includes(PropertyTypeEnum.APARTMENT)
    ? t('subjectApartment')
    : t('subjectGeneral');

  const brandName = pts?.brandId?.name;
  const productName =
    pts?.calculationData?.packageName ||
    pts?.building?.package?.label ||
    pts?.household?.package?.label ||
    undefined;

  const formattedAmount = formatPrice(amount);
  const monthlyNote =
    isInstallments && monthly
      ? `${formatPrice(monthly)}${t('amountPerMonthSuffix')}`
      : undefined;

  const title = variant === 'offline' ? t('titleOffline') : t('title');
  const subtitle = variant === 'offline' ? t('subtitleOffline') : t('subtitle');

  return (
    <ContainerStyle vertical align="center" gap={24}>
      <SuccessBadgeStyle>
        <IconCheck color={colors.success} width={40} height={40} />
      </SuccessBadgeStyle>

      <Flex vertical align="center" gap={8}>
        <Text type="large" bold color={colors.text1}>
          {title}
        </Text>
        <Text type="small" color={colors.text3}>
          {subtitle}
        </Text>
      </Flex>

      {offer && (
        <Card padding={16} radius={12}>
          <Flex vertical gap={12}>
            <CardTitleStyle type="base" bold color={colors.text1}>
              {t('orderDetailsTitle')}
            </CardTitleStyle>
            {brandName && (
              <>
                <DetailRow label={t('brandLabel')} value={brandName} />
                <Divider dashed color={colors.border8} />
              </>
            )}
            {productName && (
              <>
                <DetailRow label={t('productLabel')} value={productName} />
                <Divider dashed color={colors.border8} />
              </>
            )}
            <DetailRow label={t('subjectLabel')} value={subject} />
            <Divider dashed color={colors.border8} />
            <DetailRow
              label={t('paymentMethodLabel')}
              value={isInstallments ? t('paymentInstallments') : t('paymentOneTime')}
            />
            {formattedAmount && (
              <>
                <Divider dashed color={colors.border8} />
                <DetailRow label={t('amountLabel')} value={formattedAmount} note={monthlyNote} />
              </>
            )}
          </Flex>
        </Card>
      )}

      <Card padding={16} radius={12} background={colors.background6}>
        <Flex vertical gap={8}>
          <Text type="base" bold color={colors.text1}>
            {t('nextStepsTitle')}
          </Text>
          <Text type="small" color={colors.text2}>
            {t('emailInfo')}
          </Text>
          <Text type="small" color={colors.text3}>
            {t('spamNote')}
          </Text>
        </Flex>
      </Card>

      <Flex vertical align="center" gap={8}>
        <Text type="base" bold color={colors.text1} style={{ textAlign: 'center' }}>
          {t('supportTitle')}
        </Text>
        <Flex align="center" justify="center" gap={16} wrap="wrap">
          <InfoLinkStyle href="tel:+38515550666">
            <Flex align="center" gap={8}>
              <IconPhoneCall color={colors.primary} width={16} height={16} />
              <Text type="small" color={colors.primary} bold>
                {t('supportPhone')}
              </Text>
            </Flex>
          </InfoLinkStyle>
          <InfoLinkStyle href={`mailto:${t('supportEmail')}`}>
            <Flex align="center" gap={8}>
              <IconLetter color={colors.primary} width={16} height={16} />
              <Text type="small" color={colors.primary} bold>
                {t('supportEmail')}
              </Text>
            </Flex>
          </InfoLinkStyle>
        </Flex>
        <Text type="tiny" color={colors.text4}>
          {t('workingHours')}
        </Text>
      </Flex>

      <Button btnType="primary" size="large" onClick={navigateToHome} text={t('homeButton')} />
    </ContainerStyle>
  );
};
